import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import ScoreCard from './ScoreCard';
import StatusBadge from './StatusBadge';
import LoadingSpinner from './LoadingSpinner';
import { vendorApi } from '../services/vendor';
import { scoringApi } from '../services/scoring';

interface GuardDetailModalProps {
  guardId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function GuardDetailModal({ guardId, isOpen, onClose }: GuardDetailModalProps) {
  const navigate = useNavigate();

  const { data: guard, isLoading } = useQuery({
    queryKey: ['vendor', 'guards', guardId],
    queryFn: () => vendorApi.getGuard(guardId).then((r) => r.data),
    enabled: isOpen && !!guardId,
  });

  const { data: scoreHistory } = useQuery({
    queryKey: ['vendor', 'guard-score-history', guardId],
    queryFn: () => scoringApi.getVendorScoreHistory('guard', guardId),
    enabled: isOpen && !!guardId,
  });

  const snapshots = scoreHistory?.results ?? [];
  const previousScore = snapshots.length >= 2 ? Number(snapshots[snapshots.length - 2].score_value) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={guard?.name || 'Guard Details'} size="lg">
      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : guard ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
                <span className="text-base font-bold text-white">
                  {guard.first_name?.[0]}{guard.last_name?.[0]}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{guard.name}</h3>
                {guard.external_id && <p className="text-xs text-slate-400">ID: {guard.external_id}</p>}
              </div>
            </div>
            <ScoreCard label="Score" score={guard.score} previousScore={previousScore} />
          </div>

          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Email</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Phone</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge label={guard.is_active ? 'Active' : 'Inactive'} variant={guard.is_active ? 'success' : 'neutral'} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Sites</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.assigned_sites?.length ?? 0} assigned</dd>
            </div>
          </dl>

          {guard.assigned_sites && guard.assigned_sites.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">Assigned Sites</h4>
              <div className="space-y-1">
                {guard.assigned_sites.map((site) => (
                  <div key={site.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-sm">
                    <span className="text-slate-900">{site.name}</span>
                    <span className="text-xs text-slate-400">{site.city}, {site.state}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={() => { onClose(); navigate(`/vendor/guards/${guardId}`); }}
              className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-xl hover:bg-brand-100"
            >
              Open Full Page
            </button>
            <button
              onClick={() => { onClose(); navigate(`/vendor/guards/${guardId}/edit`); }}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
            >
              Edit Guard
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-4">Guard not found.</p>
      )}
    </Modal>
  );
}
